import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translations, type TranslationKey } from '../data/translations';
import type { Bi, Lang } from '../types';
interface LanguageValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** Translate a UI key. */
  t: (key: TranslationKey) => string;
  /** Resolve a bilingual content string from the data layer. */
  b: (value: Bi) => string;
}
const LanguageContext = createContext<LanguageValue | null>(null);
const STORAGE_KEY = 'ig-lang';
function readStoredLang(fallback: Lang): Lang {
  if (typeof window === 'undefined') return fallback;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'es') return stored;
  return fallback;
}
export function LanguageProvider({
  children,
  initialLang = 'en'
}: {
  children: React.ReactNode;
  initialLang?: Lang;
}) {
  const [lang, setLangState] = useState<Lang>(() => readStoredLang(initialLang));
  useEffect(() => {
    document.documentElement.lang = lang;
    window.localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);
  const setLang = useCallback((next: Lang) => setLangState(next), []);
  const t = useCallback((key: TranslationKey) => translations[key][lang], [lang]);
  const b = useCallback((value: Bi) => value[lang], [lang]);
  const value = useMemo(() => ({
    lang,
    setLang,
    t,
    b
  }), [lang, setLang, t, b]);
  return <LanguageContext.Provider value={value} data-id="element-430" data-mp="2yyht52gk4k57">{children}</LanguageContext.Provider>;
}
export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}